import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useStructureModeling } from "../../structureModelingContext";

export function EstimationRevisionSelector() {
  const {
    estimationTable,
    setEstimationTable,
    selectedEstimationRevision,
    setSelectedEstimationRevision,
  } = useStructureModeling();

  const initialized = useRef(false);

  const revisions = useMemo(() => {
    return Object.keys(estimationTable || {}).filter(
      (r) => r !== undefined && r !== null,
    );
  }, [estimationTable]);

  const sortedRevisions = useMemo(() => {
    return [...revisions].sort((a, b) => Number(b) - Number(a));
  }, [revisions]);

  const hasValidRevisions = revisions.length > 0;

  const getLatestRevision = (revList) => {
    return [...revList]
      .filter((r) => !isNaN(Number(r)))
      .sort((a, b) => Number(b) - Number(a))[0];
  };

  useEffect(() => {
    if (!initialized.current && hasValidRevisions) {
      initialized.current = true;

      const latestRev = getLatestRevision(revisions);

      if (latestRev && selectedEstimationRevision !== latestRev) {
        setSelectedEstimationRevision(latestRev);
      }
    }
  }, [
    revisions,
    hasValidRevisions,
    selectedEstimationRevision,
    setSelectedEstimationRevision,
  ]);

  function handleAddRev(e) {
    const value = e.target.value;

    if (value === "__new") {
      const revNumbers = revisions
        .filter((r) => !isNaN(Number(r)))
        .map(Number)
        .sort((a, b) => a - b);

      const nextRev = String(
        revNumbers.length > 0 ? Math.max(...revNumbers) + 1 : 1,
      );

      setEstimationTable((prev) => ({
        ...prev,
        [nextRev]: JSON.parse(
          JSON.stringify(prev[selectedEstimationRevision] || {}),
        ),
      }));

      setSelectedEstimationRevision(nextRev);
    } else {
      setSelectedEstimationRevision(value);
    }
  }

  function handleDeleteRevision(e, revToDelete) {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = window.confirm(
      `Are you sure you want to delete revision ${revToDelete}?`,
    );

    if (!confirmed) return;

    const remainingRevisions = revisions.filter((rev) => rev !== revToDelete);
    const nextSelectedRevision = getLatestRevision(remainingRevisions) || "";

    setEstimationTable((prev) => {
      const updated = { ...(prev || {}) };
      delete updated[revToDelete];
      return updated;
    });

    if (selectedEstimationRevision === revToDelete) {
      setSelectedEstimationRevision(nextSelectedRevision);
    }
  }

  if (!hasValidRevisions) {
    return (
      <Box display="flex" alignItems="center" gap={2} justifyContent="center">
        <FormControl size="small" disabled>
          <InputLabel>Revision</InputLabel>
          <Select value="" label="Revision" sx={{ minWidth: 140, height: 40 }}>
            <MenuItem value="">No Revisions</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }

  const currentValue = revisions.includes(selectedEstimationRevision)
    ? selectedEstimationRevision
    : sortedRevisions[0] || "";

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      justifyContent="center"
      sx={{
        p: 0.7,
      }}
    >
      <FormControl size="small">
        <InputLabel>Revision</InputLabel>

        <Select
          value={currentValue}
          onChange={handleAddRev}
          label="Revision"
          renderValue={(value) => value}
          sx={{
            minWidth: 180,
            height: 40,
          }}
        >
          {sortedRevisions.map((rev) => (
            <MenuItem
              value={rev}
              key={rev}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pr: 1,
              }}
            >
              <Typography component="span">{rev}</Typography>

              <Tooltip title="Delete revision" arrow placement="right">
                <IconButton
                  size="small"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => handleDeleteRevision(e, rev)}
                  sx={{
                    width: 28,
                    height: 28,
                    color: "error.dark",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: "error.main",
                      backgroundColor: "action.hover",
                      transform: "scale(1.08)",
                    },
                  }}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))}

          <MenuItem value="__new" sx={{ fontStyle: "italic", color: "gray" }}>
            + Add New Revision
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}